import React, { useMemo } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { UploadDrawerContent } from './UploadDrawerContent';
import {
  UploadDrawerShell,
  UploadDrawerSkeletonContent,
} from './UploadDrawerShell';
import { useLatestMeshDocumentMetadata } from 'packages/dataPipeline/hooks/useLatestMeshDocumentMetadata';
import { useGetBorrowerPipelineRunsQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { SelectedMeshRun } from '~/packages/dataPipeline/types/dataPipelineTypes';

export const UploadDrawerExistingRun = ({
  organizationId,
  onClose,
  selectedMeshRun,
}: {
  organizationId: string;
  onClose: () => void;
  selectedMeshRun: SelectedMeshRun;
}) => {
  return (
    <UploadDrawerShell onClose={onClose}>
      <UploadDrawerContent
        organizationId={organizationId}
        onClose={onClose}
        meshRunId={selectedMeshRun.meshRunId}
        meshDocumentMetadata={
          selectedMeshRun.meshRunParams.meshDocumentMetadata
        }
        rdfv={selectedMeshRun.meshRunParams.rdfv}
      />
    </UploadDrawerShell>
  );
};

// start new run for given credit facility and (optionally reporting date)
const UploadDrawerForCreditFacility = ({
  organizationId,
  creditFacilityId,
  reportingDate,
  onClose,
}: {
  organizationId: string;
  creditFacilityId: string;
  reportingDate?: Util.FullDateStr;
  onClose: () => void;
}) => {
  // find meshDocumentId for the creditFacilityId
  const { facilities } = useCreditFacilitiesOrThrow();
  const creditFacility = facilities.find((cf) => cf.id === creditFacilityId);

  finleyAssert(
    creditFacility,
    `UploadDrawerNewRunForCFAndDate: Credit facility not found for [id:${creditFacilityId}]`,
  );

  const {
    latestMeshDocumentMetadata,
    loading: meshDocumentsLoading,
  } = useLatestMeshDocumentMetadata({
    organizationId,
  });

  const meshDocumentMetadata = latestMeshDocumentMetadata?.find(
    (latestDocument) => latestDocument.cfSlugs.includes(creditFacility.slug),
  );

  // Query for latest file version, executed only when mesh document data is ready
  const { data, loading: pipelinesLoading } = useGetBorrowerPipelineRunsQuery({
    skip: !meshDocumentMetadata || !reportingDate,
    variables: {
      borrowerOrganizationId:
        meshDocumentMetadata?.borrowerOrganizationId || '',
      startDate: reportingDate,
      endDate: reportingDate,
    },
    fetchPolicy: 'network-only',
  });

  const rdfvMemo = useMemo(() => {
    if (!reportingDate) {
      return {
        loading: false,
        rdfv: null,
      };
    }

    if (data && data.getPipelineRuns) {
      const foundRun = data.getPipelineRuns.find(
        (run) => run.reportingDate === reportingDate,
      );
      if (foundRun) {
        return {
          loading: false,
          rdfv: {
            reportingDate,
            fileVersion: foundRun.fileVersion + 1,
          },
        };
      }
    }

    return {
      loading: true,
      rdfv: null,
    };
  }, [data, reportingDate]);

  const loading =
    meshDocumentsLoading ||
    pipelinesLoading ||
    rdfvMemo.loading ||
    !meshDocumentMetadata;

  return (
    <UploadDrawerShell onClose={onClose}>
      {loading ? (
        <UploadDrawerSkeletonContent onClose={onClose} />
      ) : (
        <UploadDrawerContent
          organizationId={organizationId}
          onClose={onClose}
          meshRunId={null}
          meshDocumentMetadata={meshDocumentMetadata || null}
          rdfv={rdfvMemo.rdfv}
        />
      )}
    </UploadDrawerShell>
  );
};

export const UploadDrawer = ({
  organizationId,
  creditFacilityId,
  reportingDate,
  onClose,
}: {
  organizationId: string;
  creditFacilityId?: string;
  reportingDate?: Util.FullDateStr;
  onClose: () => void;
}) => {
  const { loading } = useLatestMeshDocumentMetadata({
    organizationId,
    // when loading drawer force network fetch to get latest mesh documents
    forceNetworkFetch: true,
  });

  if (loading) {
    return (
      <UploadDrawerShell onClose={onClose}>
        <UploadDrawerSkeletonContent onClose={onClose} />
      </UploadDrawerShell>
    );
  }

  if (creditFacilityId) {
    return (
      <UploadDrawerForCreditFacility
        organizationId={organizationId}
        creditFacilityId={creditFacilityId}
        reportingDate={reportingDate}
        onClose={onClose}
      />
    );
  }

  return (
    <UploadDrawerShell onClose={onClose}>
      <UploadDrawerContent
        organizationId={organizationId}
        onClose={onClose}
        meshRunId={null}
        meshDocumentMetadata={null}
        rdfv={null}
      />
    </UploadDrawerShell>
  );
};
